/* Dr.Primal — the capture engine.

   An email-capture popup built from interchangeable parts. This file is the only part
   that knows the ORDER of things: whether the popup may open, when, which step comes
   next, and what gets recorded. Everything else plugs in:

   - STEPS  (`dp-capture-step-<name>.js`) are the game, the email form, the reward. Each
     registers a factory; the engine hands it its Liquid-rendered root element and a
     context, and waits for it to finish. The context is a step's ONLY channel — after
     registering, a step never reads `window.dpCapture`:
       ctx.config  ctx.data  ctx.adapters.{crm,shop,analytics}  ctx.track(event, props)
       ctx.events (event names)  ctx.helpers.{subscriptionFor,isBotSubmission}
       ctx.reducedMotion
   - ADAPTERS talk to the outside world, behind three fixed interfaces:
       crm       `dp-capture-klaviyo.js`   subscribe(), identify(), event(), isIdentified()
       shop      `dp-capture-shopify.js`   consentReady(), applyDiscount()
       analytics `dp-capture-posthog.js`   track()
     Steps and adapters never talk to each other except through the context the engine
     gives a step. Replacing the game is one new step file plus `FLOW`; replacing Klaviyo
     is one adapter. Neither edits this file.

   The lifecycle, per page view:

     page load ── dp-consent.js (layout, deferred first) ── dp-capture*.js register
                                                                      │
     DOMContentLoaded ── boot: no `dialog[data-dp-capture]` on the page? ── stop, silently
                               │
                               ▼
                         CONFIG valid, every variant included? every FLOW step registered
                         and in the markup?
                               │ no → console.error naming the key; never opens
                               ▼
     variant: the one this visitor was dealt before, else a fresh draw by WEIGHT, kept in
     localStorage; its keys are laid over CONFIG for everything below. ?dp-capture-variant=
     <name> shows one for this page view without keeping it
     ?dp-capture-game=scratch|cards ─► that game replaces FLOW's first step (preview both)
     ?dp-capture=reset ─► forget the frequency record, then carry on as a fresh visitor
                               │
       1 frequency   submitted → never; dismissed → hidden for COOLDOWN_DAYS   ┐ skipped by
       2 identified  HIDE_IF_IDENTIFIED and klaviyo.js knows them → hidden     ┘ ?dp-capture=force
       3 consent     shop.consentReady() → dpConsent.ready(): PostHog is told first,
                     then this resolves. Never skipped, not even by force.
       4 trigger     the clock starts only now: DELAY_SECONDS, SCROLL_PERCENT or exit
                     intent, whichever armed one is first (skipped by force)
                               │
                               ▼
                         showModal() ── capture_shown
                               │
                   ┌──► step n: capture_step_viewed ── mount() ──┐
                   │                                            ├ resolves → capture_step_completed,
                   └──────────── next step ◄────────────────────┘   result merged into ctx.data
                               │            mount() rejects → capture_error, close,
                               │            dismissedAt (a broken step cannot re-open)
                               ▼
                   the last step mounts → submittedAt (the flow got that far: never again)
                   the last step resolves → the dialog closes
                   closed by the visitor before the last step → capture_dismissed, dismissedAt

   Markup contract for `sections/dp-capture.liquid`:

   - One `<dialog data-dp-capture>`, with an inner wrapper covering its whole box — a
     click landing on the dialog element itself is read as the backdrop.
   - One `[data-dp-capture-step="<name>"]` per FLOW step, rendered `hidden`, with
     `tabindex="-1"` so focus can land on it when it is shown. A `[data-dp-capture-focus]`
     inside it, if present, takes focus instead.
   - Any `[data-dp-capture-close]` closes the dialog.

   Liquid writes every word. This file contains no user-facing English: it shows, hides
   and records. Focus trapping, Escape, the inert page and the backdrop are native
   `<dialog>`. Added here: restoring the scroll position, which `showModal()` does not
   do on iOS (as in `dp-benefit-modal.js`), and moving focus to each step as it is
   shown, since the control that had it has just been hidden.

   `window.dpCapture` is the surface for files at load time: registerAdapter /
   registerStep, `config`, `EVENTS`, and `fetchWithin` (the adapters' timed fetch; steps
   never fetch). `subscriptionFor` (the Klaviyo data contract) and `isBotSubmission` (the
   email step's guard) are also here, but steps get them from `ctx.helpers`. The
   decision logic is plain functions on `dpCapture.core`, so `tests/dp-capture/` can run
   this exact file under Node; nothing in the theme calls `core`. */
(function () {
  'use strict';

  /* ---- The campaign. The only block an owner normally edits. -------------------- */
  var CONFIG = {
    ENABLED: false,                             // false: the popup never opens; adapters still register for the quiz
    CAMPAIGN: 'scratch-v1',                     // names the frequency record and every event; a new value shows the popup to everyone again
    FLOW: ['cards', 'email', 'reward'],         // step order; names match registered steps. The game ('cards' or 'scratch') comes first
    DISCOUNT_CODE: '',                          // '' = no code: the last step shows the saving and sends nothing to the cart.
                                                // A code must exist in Shopify first (WELCOME25 does: docs/store-resource-ids.md)
    KLAVIYO_COMPANY_ID: 'VkaMvV',               // public site id, safe in page source
    KLAVIYO_LIST_ID: 'WsSRkS',                  // the live Dr.Primal signup list (single opt-in). QA list: 'XFaZtA' ('Dr.Primal capture test')
    KLAVIYO_SOURCE: 'Dr.Primal scratch card',   // shows as the signup source in Klaviyo
    // When it opens: whichever armed trigger fires first. null turns a trigger off; at least one must stay on.
    DELAY_SECONDS: 8,                           // opens this long after consent is decided; 0 opens at once
    SCROLL_PERCENT: 40,                         // opens once this much of the page has been scrolled; 0 opens with consent
    EXIT_INTENT: false,                         // true also opens when the pointer leaves through the top edge. Desktop only: on a touch
                                                // screen it is ignored, or, if SCROLL_PERCENT is null, becomes EXIT_TOUCH_SCROLL_PERCENT
    COOLDOWN_DAYS: 7,                           // how long a dismissal hides the popup for this campaign
    HIDE_IF_IDENTIFIED: true,                   // skip visitors klaviyo.js already knows; skipped by ?dp-capture=force

    // The A/B test. Each visitor is dealt one variant by WEIGHT and keeps it. A variant overrides any key above
    // (FLOW, DELAY_SECONDS, SCROLL_PERCENT, EXIT_INTENT, ...). Every event carries `variant` and `$feature/<EXPERIMENT>`,
    // which is what a PostHog Experiment reads. PostHog needs one variant named 'control'.
    EXPERIMENT: '',                             // the PostHog experiment's flag key, e.g. 'dp-capture-ab-1'; a new value re-deals everyone. '' = no test: everyone gets FLOW above and VARIANTS is ignored
    VARIANTS: {
      control: { WEIGHT: 50, FLOW: ['cards', 'email', 'reward'] },
      scratch: { WEIGHT: 50, FLOW: ['scratch', 'email', 'reward'] }
    }
  };

  /* ---- Tuning. Change with care; each says what moves with it. ------------------ */
  var STORAGE_PREFIX = 'dp-capture:';           // localStorage key is this + CAMPAIGN; changing it forgets every visitor's record
  var FORCE_PARAM = 'dp-capture';               // QA query parameter; the two values it takes follow
  var FORCE_VALUE = 'force';                    // ?dp-capture=force skips frequency, identified and trigger — never consent
  var RESET_VALUE = 'reset';                    // ?dp-capture=reset forgets this campaign's frequency record
  var GAME_PARAM = 'dp-capture-game';           // QA/preview: ?dp-capture-game=scratch|cards swaps the game at the front of FLOW for one page view
  var VARIANT_PARAM = 'dp-capture-variant';     // QA: ?dp-capture-variant=<name> shows that variant for one page view, without keeping it
  var VARIANT_KEY = 'variant:';                 // localStorage key is STORAGE_PREFIX + this + EXPERIMENT
  var FEATURE_PREFIX = '$feature/';             // PostHog's experiment property: $feature/<flag key> = variant name
  var DAY_MS = 24 * 60 * 60 * 1000;             // the unit of COOLDOWN_DAYS
  var EXIT_EDGE_PX = 0;                         // a pointer leaving at or above this clientY reads as exit intent
  var EXIT_TOUCH_SCROLL_PERCENT = 50;           // exit intent on a touch screen, when SCROLL_PERCENT is off: opens at this scroll depth
  var TOUCH_QUERY = '(hover: none)';            // a device matching this has no pointer to leave the window with
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'; // handed to steps as ctx.reducedMotion
  var DIALOG_SELECTOR = 'dialog[data-dp-capture]'; // the section's dialog; must match sections/dp-capture.liquid
  var STEP_ATTRIBUTE = 'data-dp-capture-step';  // a step's root carries its FLOW name in this; must match the section
  var CLOSE_SELECTOR = '[data-dp-capture-close]'; // any control that closes the dialog; must match the section
  var FOCUS_SELECTOR = '[data-dp-capture-focus]'; // the element inside a step that takes focus when it is shown
  var OPEN_CLASS = 'dp-capture-open';           // on <body> while open; the section's CSS stops the page scrolling under it
  var REQUIRED_ADAPTERS = ['crm', 'shop', 'analytics']; // boot refuses to open without all three registered
  var PROFILE_CAMPAIGN_KEY = 'dp_capture_campaign'; // Klaviyo profile property; segments and flows filter on this name
  var PROFILE_OFFER_KEY = 'dp_capture_offer';   // Klaviyo profile property; renaming either orphans existing profiles

  // Event names. The PostHog dashboard (tools/posthog/capture-dashboard.mjs) queries
  // these strings; rename one there in the same commit. The last two are sent by steps.
  var EVENTS = {
    SHOWN: 'capture_shown',
    STEP_VIEWED: 'capture_step_viewed',
    STEP_COMPLETED: 'capture_step_completed',
    DISMISSED: 'capture_dismissed',
    ERROR: 'capture_error',
    SUBMIT_FAILED: 'capture_submit_failed',
    CODE_APPLIED: 'capture_code_applied',
  };

  // The global itself is the load-once guard: a second copy of this file must not
  // replace a registry the adapters and steps have already filled.
  if (window.dpCapture) return;

  var adapters = {};
  var steps = {};

  /* ---- Pure decision logic ------------------------------------------------------- */

  function merge(target, source) {
    if (!source) return target;
    Object.keys(source).forEach(function (key) {
      target[key] = source[key];
    });
    return target;
  }

  // A step's promise may come back from a throw, a rejection or a plain value; this
  // turns all three into one promise so nothing can escape as a synchronous error.
  function attempt(call) {
    try {
      return Promise.resolve(call());
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /* A fetch that settles within `timeoutMs`: the Response, or null when there was no
     answer — offline, blocked by an extension, or cut off, in which case the request is
     aborted too. Never rejects. The adapters share it so the timeout rule is written
     once; each passes its own timeout constant. */
  function fetchWithin(url, init, timeoutMs) {
    return new Promise(function (resolve) {
      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var timer = window.setTimeout(function () {
        if (controller) controller.abort();
        resolve(null);
      }, timeoutMs);

      function finish(response) {
        window.clearTimeout(timer);
        resolve(response);
      }

      attempt(function () {
        return window.fetch(url, merge(merge({}, init), { signal: controller ? controller.signal : undefined }));
      }).then(finish, function () {
        finish(null);
      });
    });
  }

  /* `FLOW` as an array of registered, distinct step names. A comma string is accepted
     too, so a flow can come from anywhere that only holds text. */
  function parseFlow(value, registered) {
    var names = typeof value === 'string' ? value.split(',') : value;
    if (!Array.isArray(names) || !names.length) return { flow: [], error: 'FLOW is empty' };

    var flow = [];
    for (var i = 0; i < names.length; i += 1) {
      var name = typeof names[i] === 'string' ? names[i].trim() : '';
      if (!name) return { flow: [], error: 'FLOW has an empty step name at position ' + i };
      if (!Object.prototype.hasOwnProperty.call(registered, name)) {
        return { flow: [], error: 'FLOW names an unregistered step "' + name + '"' };
      }
      if (flow.indexOf(name) !== -1) return { flow: [], error: 'FLOW repeats step "' + name + '"' };
      flow.push(name);
    }
    return { flow: flow, error: '' };
  }

  function own(object, key) {
    return Boolean(object) && typeof key === 'string' && Object.prototype.hasOwnProperty.call(object, key);
  }

  function isCount(value) {
    return typeof value === 'number' && isFinite(value) && value >= 0;
  }

  /* The problems in one flat config (CONFIG, or CONFIG with a variant laid over it). */
  function keyErrors(config, registeredSteps) {
    var errors = [];

    ['CAMPAIGN', 'KLAVIYO_COMPANY_ID', 'KLAVIYO_LIST_ID', 'KLAVIYO_SOURCE'].forEach(function (key) {
      if (typeof config[key] !== 'string' || !config[key].trim()) errors.push(key + ' is empty');
    });
    if (typeof config.DISCOUNT_CODE !== 'string' || config.DISCOUNT_CODE.trim() !== config.DISCOUNT_CODE) {
      errors.push("DISCOUNT_CODE must be a code without spaces around it, or '' for none");
    }
    if (!isCount(config.COOLDOWN_DAYS)) errors.push('COOLDOWN_DAYS must be a number of zero or more');
    ['DELAY_SECONDS', 'SCROLL_PERCENT'].forEach(function (key) {
      if (config[key] !== null && !isCount(config[key])) errors.push(key + ' must be a number of zero or more, or null');
    });
    if (config.SCROLL_PERCENT > 100) errors.push('SCROLL_PERCENT must be 100 or less');
    ['EXIT_INTENT', 'HIDE_IF_IDENTIFIED'].forEach(function (key) {
      if (typeof config[key] !== 'boolean') errors.push(key + ' must be true or false');
    });
    if (config.DELAY_SECONDS === null && config.SCROLL_PERCENT === null && config.EXIT_INTENT === false) {
      errors.push('DELAY_SECONDS, SCROLL_PERCENT and EXIT_INTENT are all off: the popup could never open');
    }

    var parsed = parseFlow(config.FLOW, registeredSteps);
    if (parsed.error) errors.push(parsed.error);
    return errors;
  }

  /* CONFIG with one variant's keys laid over it; CONFIG as it is for '' or an unknown name. */
  function configFor(config, name) {
    var effective = merge({}, config);
    if (!own(config.VARIANTS, name)) return effective;
    var variant = config.VARIANTS[name];
    Object.keys(variant).forEach(function (key) {
      if (key !== 'WEIGHT') effective[key] = variant[key];
    });
    return effective;
  }

  /* The problems in EXPERIMENT and VARIANTS, each variant checked as the config it makes. */
  function variantErrors(config, registeredSteps) {
    if (typeof config.EXPERIMENT !== 'string') return ["EXPERIMENT must be text; '' turns the test off"];
    if (!config.EXPERIMENT) return [];
    var variants = config.VARIANTS;
    var names = variants && typeof variants === 'object' ? Object.keys(variants) : [];
    if (!names.length) return ['VARIANTS is empty while EXPERIMENT is set'];

    var errors = [];
    var total = 0;
    if (!own(variants, 'control')) errors.push('VARIANTS needs one named "control" (PostHog requires it)');
    names.forEach(function (name) {
      var label = 'VARIANTS.' + name;
      var variant = variants[name];
      if (!variant || typeof variant !== 'object') {
        errors.push(label + ' must be an object');
        return;
      }
      if (isCount(variant.WEIGHT)) total += variant.WEIGHT;
      else errors.push(label + '.WEIGHT must be a number of zero or more');
      Object.keys(variant).forEach(function (key) {
        if (key === 'WEIGHT') return;
        if (!own(config, key) || key === 'EXPERIMENT' || key === 'VARIANTS') {
          errors.push(label + '.' + key + ' is not a CONFIG key a variant can set');
        }
      });
      keyErrors(configFor(config, name), registeredSteps).forEach(function (message) {
        errors.push(label + ': ' + message);
      });
    });
    if (!(total > 0)) errors.push('VARIANTS weights add up to zero');
    return errors;
  }

  /* Every problem, each message led by the CONFIG key (or adapter) at fault. */
  function validateConfig(config, registeredSteps, registeredAdapters) {
    var errors = keyErrors(config, registeredSteps).concat(variantErrors(config, registeredSteps));
    REQUIRED_ADAPTERS.forEach(function (name) {
      if (!registeredAdapters[name]) errors.push('adapter "' + name + '" is not registered');
    });
    return errors;
  }

  /* A variant name drawn by WEIGHT; `roll` is a number in [0, 1). A 0 weight is never drawn. */
  function pickVariant(variants, roll) {
    var names = Object.keys(variants).filter(function (name) {
      return variants[name].WEIGHT > 0;
    });
    var point = roll * names.reduce(function (sum, name) {
      return sum + variants[name].WEIGHT;
    }, 0);
    for (var i = 0; i < names.length; i += 1) {
      point -= variants[names[i]].WEIGHT;
      if (point < 0) return names[i];
    }
    return names[names.length - 1];
  }

  /* The visitor's variant: the QA parameter's (even at weight 0), else the one they were
     dealt before while it still has weight, else a fresh draw. */
  function chooseVariant(variants, forced, stored, roll) {
    if (own(variants, forced)) return forced;
    if (own(variants, stored) && variants[stored].WEIGHT > 0) return stored;
    return pickVariant(variants, roll);
  }

  /* The value of one query parameter in a location.search string, or ''. */
  function queryValue(search, key) {
    var pairs = String(search || '').replace(/^\?/, '').split('&');
    for (var i = 0; i < pairs.length; i += 1) {
      var parts = pairs[i].split('=');
      if (parts[0] === key) return parts[1] || '';
    }
    return '';
  }

  /* 'force', 'reset' or '' from a location.search string. */
  function readMode(search) {
    var value = queryValue(search, FORCE_PARAM);
    return value === FORCE_VALUE || value === RESET_VALUE ? value : '';
  }

  /* ?dp-capture-game=<name>: puts that game in place of the flow's first step, but only
     when both are registered games. Anything else leaves the flow as it is. */
  function withGame(flow, game, registered) {
    var isGame = function (name) {
      return Boolean(name && registered[name] && registered[name].game);
    };
    if (!isGame(game) || !isGame(flow[0]) || flow.indexOf(game) > 0) return flow;
    return [game].concat(flow.slice(1));
  }

  /* Why the frequency rule hides the popup ('submitted' / 'cooldown'), or '' to show. */
  function frequencyBlock(record, now, cooldownDays) {
    if (record.submittedAt) return 'submitted';
    if (record.dismissedAt && now - record.dismissedAt < cooldownDays * DAY_MS) return 'cooldown';
    return '';
  }

  /* Private mode and blocked storage throw on access, even on reading
     `window.localStorage`. This is the storage, or null. */
  function storageFrom(getStorage) {
    try {
      return getStorage() || null;
    } catch (error) {
      return null;
    }
  }

  /* Deals the visitor's variant and keeps it, except one the QA parameter forced. '' when
     the test is off. Blocked storage deals afresh each page view, still a fair split. */
  function assignVariant(config, getStorage, forced, roll) {
    if (!config.EXPERIMENT) return '';
    var key = STORAGE_PREFIX + VARIANT_KEY + config.EXPERIMENT;
    var store = storageFrom(getStorage);
    var stored = '';
    try {
      stored = (store && store.getItem(key)) || '';
    } catch (error) {
      /* unreadable reads as never dealt */
    }
    var name = chooseVariant(config.VARIANTS, forced, stored, roll);
    if (store && name !== stored && name !== forced) {
      try {
        store.setItem(key, name);
      } catch (error) {
        /* quota or blocked: dealt again next page view */
      }
    }
    return name;
  }

  /* The per-campaign `{ dismissedAt, submittedAt }` record. Every storage failure here
     reads as "a fresh visitor", which shows the popup rather than blocking it. */
  function createStore(getStorage, key) {
    function storage() {
      return storageFrom(getStorage);
    }

    function read() {
      var store = storage();
      var record = {};
      if (!store) return record;
      try {
        var parsed = JSON.parse(store.getItem(key));
        if (parsed && typeof parsed === 'object') {
          // Only finite numbers count: a corrupted value must not hide the popup forever.
          ['dismissedAt', 'submittedAt'].forEach(function (field) {
            if (typeof parsed[field] === 'number' && isFinite(parsed[field])) record[field] = parsed[field];
          });
        }
      } catch (error) {
        /* unreadable reads as fresh */
      }
      return record;
    }

    function mark(field, now) {
      var store = storage();
      if (!store) return;
      var record = read();
      record[field] = now;
      try {
        store.setItem(key, JSON.stringify(record));
      } catch (error) {
        /* quota or blocked: the record is lost, the page carries on */
      }
    }

    return {
      read: read,
      markDismissed: function (now) {
        mark('dismissedAt', now);
      },
      markSubmitted: function (now) {
        mark('submittedAt', now);
      },
      clear: function () {
        var store = storage();
        if (!store) return;
        try {
          store.removeItem(key);
        } catch (error) {
          /* nothing stored that could be cleared */
        }
      },
    };
  }

  /* Display rules 1-3, in order; the first failure stops this page view. Resolves
     `{ show, reason }`. Rejects only when consent cannot be determined at all, which
     the caller logs — the popup then never opens, failing closed. Rule 4, the trigger,
     is `waitForTrigger`, started by the caller only after this resolves `show: true`. */
  function evaluateRules(options) {
    var config = options.config;
    var force = options.mode === FORCE_VALUE;
    if (options.mode === RESET_VALUE) options.store.clear();

    if (!force) {
      var blocked = frequencyBlock(options.store.read(), options.now, config.COOLDOWN_DAYS);
      if (blocked) return Promise.resolve({ show: false, reason: blocked });
    }

    var crm = options.crm;
    var checkIdentified =
      !force && config.HIDE_IF_IDENTIFIED && crm && typeof crm.isIdentified === 'function';
    var identified = checkIdentified
      ? attempt(function () {
          return crm.isIdentified();
        }).then(
          function (known) {
            return known === true;
          },
          function () {
            return false;
          }
        )
      : Promise.resolve(false);

    return identified.then(function (known) {
      if (known) return { show: false, reason: 'identified' };
      return attempt(function () {
        return options.shop.consentReady();
      }).then(function () {
        return { show: true, reason: force ? FORCE_VALUE : '' };
      });
    });
  }

  /* The honeypot and minimum-fill-time guard for the email step. True means "a bot":
     the step then skips the network call and carries on as if it had succeeded. The
     threshold belongs to the email step (its MIN_FILL_MS); the rule belongs here. */
  function isBotSubmission(input) {
    if (input.honeypot) return true;
    return input.elapsedMs < input.minFillMs;
  }

  /* The argument for `crm.subscribe`, so the data contract with Klaviyo is written once. */
  function subscriptionFor(email, config) {
    var properties = {};
    properties[PROFILE_CAMPAIGN_KEY] = config.CAMPAIGN;
    if (config.DISCOUNT_CODE) properties[PROFILE_OFFER_KEY] = config.DISCOUNT_CODE;
    return {
      email: email,
      properties: properties,
      source: config.KLAVIYO_SOURCE,
      listId: config.KLAVIYO_LIST_ID,
    };
  }

  /* The first FLOW step registered with `{ game: true }`, or ''. */
  function gameOf(flow, registered) {
    for (var i = 0; i < flow.length; i += 1) {
      if (registered[flow[i]] && registered[flow[i]].game) return flow[i];
    }
    return '';
  }

  /* The props every event carries. Merged LAST, so a step cannot overwrite them. With a
     variant dealt, `$feature/<EXPERIMENT>` is what a PostHog Experiment counts by. */
  function baseProps(config, flow, registered, pagePath, variant) {
    var props = {
      campaign: config.CAMPAIGN,
      flow: flow.join(','),
      game: gameOf(flow, registered),
      page_path: pagePath,
    };
    if (variant) {
      props.experiment = config.EXPERIMENT;
      props.variant = variant;
      props[FEATURE_PREFIX + config.EXPERIMENT] = variant;
    }
    return props;
  }

  /* Runs the flow once. `view` is the DOM seam — root(name), enter(root), leave(root),
     close() — so this runs, and is tested, without a document. `track(event, props)`
     already adds the base props. Call `start()` once the dialog is open and
     `dismiss()` from the dialog's `close` event. */
  function createSession(options) {
    var flow = options.flow;
    var data = {};
    var index = -1;
    var current = null;
    var state = 'idle';

    function scope() {
      return { step: flow[index], index: index };
    }

    function leave() {
      if (!current) return;
      var leaving = current;
      current = null;
      try {
        if (leaving.step && typeof leaving.step.unmount === 'function') leaving.step.unmount();
      } catch (error) {
        console.error('[dp-capture] step "' + leaving.name + '" failed to unmount', error);
      }
      if (leaving.root) options.view.leave(leaving.root);
    }

    function fail(error) {
      if (state !== 'running') return;
      state = 'failed';
      console.error('[dp-capture] step "' + flow[index] + '" failed', error);
      options.store.markDismissed(options.now());
      options.track(EVENTS.ERROR, scope());
      leave();
      options.view.close();
    }

    function next() {
      if (state !== 'running') return;
      index += 1;
      if (index >= flow.length) {
        state = 'done';
        options.view.close();
        return;
      }

      var name = flow[index];
      var position = index;
      // Reaching the last step means every step before it succeeded — for the shipped
      // flow, a subscription Klaviyo accepted. That is what "submitted" means.
      if (position === flow.length - 1) options.store.markSubmitted(options.now());

      var root = options.view.root(name);
      var ctx = {
        config: options.config,
        data: data,
        adapters: options.adapters,
        track: function (event, props) {
          options.track(event, merge(merge({}, props), { step: name, index: position }));
        },
        events: EVENTS,
        helpers: {
          subscriptionFor: function (email) {
            return subscriptionFor(email, options.config);
          },
          isBotSubmission: isBotSubmission,
        },
        reducedMotion: options.reducedMotion,
      };

      attempt(function () {
        var step = options.steps[name].factory(root, ctx);
        current = { name: name, root: root, step: step };
        options.view.enter(root);
        options.track(EVENTS.STEP_VIEWED, scope());
        return step.mount();
      }).then(
        function (result) {
          if (state !== 'running' || index !== position) return;
          if (result && typeof result === 'object') merge(data, result);
          options.track(EVENTS.STEP_COMPLETED, scope());
          leave();
          next();
        },
        function (error) {
          if (index === position) fail(error);
        }
      );
    }

    return {
      start: function () {
        if (state !== 'idle') return;
        state = 'running';
        next();
      },
      dismiss: function () {
        if (state !== 'running') return;
        var terminal = index >= flow.length - 1;
        state = terminal ? 'done' : 'dismissed';
        if (!terminal) {
          options.store.markDismissed(options.now());
          options.track(EVENTS.DISMISSED, scope());
        }
        leave();
      },
      state: function () {
        return state;
      },
    };
  }

  /* ---- DOM wiring ---------------------------------------------------------------- */

  /* Which triggers are armed on this device: `{ delayMs, scroll, exit }`, null for off.
     A touch screen has no pointer to leave the window with, so exit intent is dropped
     there, and stands in as a scroll depth when it was the only way to open by scrolling. */
  function triggerPlan(config, touch) {
    var exitStandIn = config.EXIT_INTENT && touch && config.SCROLL_PERCENT === null;
    return {
      delayMs: config.DELAY_SECONDS === null ? null : config.DELAY_SECONDS * 1000,
      scroll: exitStandIn ? EXIT_TOUCH_SCROLL_PERCENT : config.SCROLL_PERCENT,
      exit: config.EXIT_INTENT && !touch,
    };
  }

  /* Rule 4. Resolves on the first armed trigger: the delay, the scroll depth or exit intent. */
  function waitForTrigger(config, touch) {
    var plan = triggerPlan(config, Boolean(touch));
    return new Promise(function (resolve) {
      var timer = null;

      function scrolledPercent() {
        var scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return 0;
        return (window.scrollY / scrollable) * 100;
      }

      function done() {
        window.clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('mouseout', onExit);
        resolve();
      }

      function onScroll() {
        if (scrolledPercent() >= plan.scroll) done();
      }

      function onExit(event) {
        if (!event.relatedTarget && event.clientY <= EXIT_EDGE_PX) done();
      }

      if (plan.delayMs !== null) timer = window.setTimeout(done, plan.delayMs);
      if (plan.exit) document.addEventListener('mouseout', onExit);
      if (plan.scroll !== null) {
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    });
  }

  function matches(query) {
    return Boolean(window.matchMedia && window.matchMedia(query).matches);
  }

  var booted = false;

  function boot() {
    if (booted) return;
    booted = true;

    var dialog = document.querySelector(DIALOG_SELECTOR);
    // No section on this page, or no <dialog> support: an enhancement that is absent,
    // not a dead control.
    if (!dialog || typeof dialog.showModal !== 'function') return;

    // Never over the quiz funnel: that page captures the same email itself, and a popup
    // opening mid-question is the one interruption it cannot afford. The adapters have
    // already registered by now, which is all dp-quiz.js needs from this file.
    if (document.querySelector('[data-dp-quiz]')) return;

    if (CONFIG.ENABLED === false) return;

    function stepRoot(name) {
      return dialog.querySelector('[' + STEP_ATTRIBUTE + '="' + name + '"]');
    }

    function getLocalStorage() {
      return window.localStorage;
    }

    var search = window.location.search;
    var errors = validateConfig(CONFIG, steps, adapters);
    // Dealt only once CONFIG is sound, so a broken VARIANTS never reaches storage.
    var variant = errors.length ? '' : assignVariant(CONFIG, getLocalStorage, queryValue(search, VARIANT_PARAM), Math.random());
    var config = configFor(CONFIG, variant);
    var flow = withGame(parseFlow(config.FLOW, steps).flow, queryValue(search, GAME_PARAM), steps);
    flow.forEach(function (name) {
      if (!stepRoot(name)) errors.push('FLOW step "' + name + '" has no [' + STEP_ATTRIBUTE + '] markup');
    });
    if (errors.length) {
      errors.forEach(function (message) {
        console.error('[dp-capture] CONFIG: ' + message);
      });
      return;
    }

    var base = baseProps(config, flow, steps, window.location.pathname, variant);
    var store = createStore(getLocalStorage, STORAGE_PREFIX + config.CAMPAIGN);
    var mode = readMode(search);
    var scrollY = 0;

    function track(event, props) {
      try {
        adapters.analytics.track(event, merge(merge({}, props), base));
      } catch (error) {
        console.error('[dp-capture] track failed', error);
      }
    }

    var session = createSession({
      flow: flow,
      steps: steps,
      config: config,
      adapters: { crm: adapters.crm, shop: adapters.shop, analytics: adapters.analytics },
      reducedMotion: matches(REDUCED_MOTION_QUERY),
      store: store,
      track: track,
      now: function () {
        return Date.now();
      },
      view: {
        root: stepRoot,
        enter: function (root) {
          root.hidden = false;
          // The control that had focus has just been hidden with the previous step;
          // without this, focus falls back to the dialog and a screen reader hears nothing.
          var target = root.querySelector(FOCUS_SELECTOR) || root;
          if (typeof target.focus === 'function') target.focus();
        },
        leave: function (root) {
          root.hidden = true;
        },
        close: function () {
          if (dialog.open) dialog.close();
        },
      },
    });

    flow.forEach(function (name) {
      stepRoot(name).hidden = true;
    });

    dialog.addEventListener('click', function (event) {
      if (!(event.target instanceof window.Element)) return;
      if (event.target.closest(CLOSE_SELECTOR)) {
        dialog.close();
        return;
      }
      // The inner wrapper covers the whole box, so a hit on the dialog is the backdrop.
      if (event.target === dialog) dialog.close();
    });

    // Every exit — a close control, Escape, the backdrop, the flow ending or failing —
    // arrives here, so the class and the scroll position cannot be left behind.
    dialog.addEventListener('close', function () {
      session.dismiss();
      document.body.classList.remove(OPEN_CLASS);
      window.scrollTo(0, scrollY);
    });

    function open() {
      if (dialog.open) return;
      scrollY = window.scrollY;
      dialog.showModal();
      document.body.classList.add(OPEN_CLASS);
      track(EVENTS.SHOWN, {});
      session.start();
    }

    evaluateRules({
      config: config,
      store: store,
      mode: mode,
      now: Date.now(),
      crm: adapters.crm,
      shop: adapters.shop,
    })
      .then(function (decision) {
        if (!decision.show) return undefined;
        return (mode === FORCE_VALUE ? Promise.resolve() : waitForTrigger(config, matches(TOUCH_QUERY))).then(open);
      })
      .catch(function (error) {
        console.error('[dp-capture] not opening: consent could not be determined', error);
      });
  }

  window.dpCapture = {
    config: CONFIG,
    EVENTS: EVENTS,
    // The live registry, not a copy: registerAdapter assigns into this same object, so
    // a reader holding the reference sees adapters as they arrive. dp-quiz.js reads
    // crm and analytics from here rather than owning a second Klaviyo client.
    adapters: adapters,
    registerAdapter: function (name, adapter) {
      adapters[name] = adapter;
    },
    // `options.game` marks the step that is the game, reported as `game` on every event.
    registerStep: function (name, factory, options) {
      steps[name] = { factory: factory, game: Boolean(options && options.game) };
    },
    subscriptionFor: function (email) {
      return subscriptionFor(email, CONFIG);
    },
    isBotSubmission: isBotSubmission,
    fetchWithin: fetchWithin,
    core: {
      parseFlow: parseFlow,
      validateConfig: validateConfig,
      configFor: configFor,
      pickVariant: pickVariant,
      chooseVariant: chooseVariant,
      assignVariant: assignVariant,
      triggerPlan: triggerPlan,
      readMode: readMode,
      withGame: withGame,
      frequencyBlock: frequencyBlock,
      createStore: createStore,
      evaluateRules: evaluateRules,
      subscriptionFor: subscriptionFor,
      baseProps: baseProps,
      createSession: createSession,
      waitForTrigger: waitForTrigger,
    },
  };

  /* NOT the `readyState === 'loading'` check `dp-benefit-modal.js` uses. While deferred
     scripts run, readyState is already 'interactive', so that check would boot here,
     before the adapter and step files after this one have registered. Deferred scripts
     all run before DOMContentLoaded, so that is the moment. `load` covers a copy of
     this file injected after DOMContentLoaded (the theme editor re-rendering the
     section); `booted` makes the two one. */
  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }
})();
